import { ApolloQueryResult, gql } from "@apollo/client";
import { BookReccomendations } from "components/books/book-reccomendations";
import { EventRecommendations } from "components/books/event-reccomendations";
import { ListReccomendations } from "components/books/list-reccomendations";
import { Button } from "components/buttons/button";
import { RenderContent } from "components/content-renderer";
import { Layout } from "components/layout";
import { SharingBar } from "components/sharing-bar";
import { Spinner } from "components/spinner";
import currency from "currency.js";
import {
  BookCardDataFragmentDoc,
  EventCardDataFragmentDoc,
  GetBookDocument,
  GetBookQueryResult,
  PostCardDataFragmentDoc,
  useGetBookBasedReccomendationsQuery,
  useGetBookQuery,
} from "lib/graphql-generated";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { useBasket } from "lib/hooks/basket/use-basket";
import { GetServerSideProps } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";

const GET_BOOK_RECS = gql`
  query GetBookBasedReccomendations(
    $isbn: bigint!
    $categories: [String!] = []
    $tags: [Int!] = []
    $offset: Int!
  ) {
    books_by_pk(isbn: $isbn) {
      is_unlisted
      price
      post_books {
        post {
          ...PostCardData
        }
      }
    }

    books(
      limit: 4
      offset: $offset
      order_by: [{ order_books_aggregate: { sum: { quantity: desc } } }]
      where: {
        _and: [
          {
            _or: [
              { category_books: { category_name: { _in: $categories } } }
              { tag_books: { tag_id: { _in: $tags } } }
            ]
          }
          { is_unlisted: { _eq: false } }
          { _or: [{ stock: { _gt: 0 } }, { track_stock: { _eq: false } }] }
          { isbn: { _neq: $isbn } }
        ]
      }
    ) {
      ...BookCardData
    }

    event_book(
      where: {
        _and: [
          { book_isbn: { _eq: $isbn } }
          { event: { is_cancelled: { _eq: false } } }
        ]
      }
      distinct_on: event_id
    ) {
      event {
        ...EventCardData
      }
    }
  }
  ${BookCardDataFragmentDoc}
  ${PostCardDataFragmentDoc}
  ${EventCardDataFragmentDoc}
`;

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const client = initializeApollo();

  try {
    const { data, error }: ApolloQueryResult<GetBookQueryResult["data"]> =
      await client.query({
        query: GetBookDocument,
        variables: {
          isbn: params.isbn,
        },
      });

    if (
      data.books_by_pk &&
      !data.books_by_pk.is_unlisted &&
      data.books_by_pk.price > 0
    ) {
      return addApolloState(client);
    } else {
      return {
        notFound: true,
      };
    }
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

export const Book = () => {
  const [basket, reducer] = useBasket();
  const routeIsbn = useRouter().query["isbn"];
  const [offset, setOffset] = useState<number>(0);
  const { data, loading, error } = useGetBookQuery({
    variables: {
      isbn: routeIsbn,
    },
  });
  const {
    data: recs,
    error: er,
    loading: recLoading,
  } = useGetBookBasedReccomendationsQuery({
    variables: {
      categories: data?.books_by_pk.category_books.map((c) => c.category.name),
      isbn: data?.books_by_pk.isbn,
      offset: offset * 4,
    },
  });

  const book = data?.books_by_pk;
  const isPreOrder = book?.tag_books.some(
    (tag) => tag.tag.text.toLowerCase() === "pre-order"
  );

  const isInBasket = (isbn: any): boolean => {
    return basket.items.some((item) => item.id === isbn);
  };

  return (
    <>
      <Layout pageName={book.title} className='flex flex-row flex-wrap'>
        {loading ? (
          <section className='w-screen-2xl mx-auto flex justify-center py-20'>
            <Spinner size={20} />
          </section>
        ) : book ? (
          <>
            <article className='[ order-1 grid grid-cols-1 gap-y-6 py-6 ] [ md:grid-cols-5 md:gap-y-8 md:max-w-screen-md md:px-4 md:pb-8 ] [ lg:max-w-screen-xl lg:grid-cols-7 lg:gap-x-8 lg:order-3 ] w-full justify-items-center mx-auto'>
              <img
                alt={`Cover for: ${book.title}`}
                className='max-w-2xs sm:max-w-xs [ md:max-w-full md:col-span-2 ] [ ]'
                src={book.image}
              />

              <section className='[ max-w-2xs sm:max-w-xs flex flex-col ] [ md:mb-auto md:flex-col md:col-span-3 ] [ lg:col-span-2 lg:col-start-6 ] flex-wrap '>
                <h1 className='max-w-2xs sm:max-w-xs'>
                  <span className='bg-green-default text-white font-header tracking-wide uppercase pt-1 px-1 whitespace-pre-wrap text-2xl'>
                    {book.title}
                  </span>
                </h1>
                <span className='w-full  uppercase font-header tracking-wide text-xl'>
                  {book.author}{" "}
                </span>
                <Link href={`/search?q=${book.author}&p=0&type=books`}>
                  <a className='pb-2 sm:pb-0 normal-case mb-2 text-sm font-sans hover:underline'>
                    More by this author...
                  </a>
                </Link>
                <span className='my-auto uppercase font-header tracking-wide text-2xl md:mt-4'>
                  {currency(
                    book.is_discounted
                      ? book.price - book.discount_value
                      : book.price,
                    {
                      symbol: "£",
                      fromCents: true,
                    }
                  ).format()}
                </span>
                <span className='my-auto'>{book.attributes}</span>
                {book.stock > 0 || book.track_stock === false ? (
                  <Button
                    className='md:mt-4 ml-auto my-auto md:ml-0'
                    buttonType='primary'
                    buttonSize='md'
                    disabled={isInBasket(book.isbn)}
                    onClick={() =>
                      reducer({
                        type: "add",
                        payload: {
                          type: "book",
                          id: book.isbn,
                          data: {
                            price: book.price,
                            discount: book.is_discounted
                              ? book.discount_value
                              : 0,
                            quantity: 1,
                            book: {
                              isbn: book.isbn,
                              image: book.image,
                              title: book.title,
                            },
                          },
                        },
                      })
                    }>
                    {isInBasket(book.isbn)
                      ? "In Basket"
                      : isPreOrder
                      ? "Pre-order"
                      : "Add to basket"}
                  </Button>
                ) : (
                  <div>
                    <p className='text-red-dark font-bold ml-2 md:ml-0'>
                      Out of stock
                    </p>
                    <p className='text-gray-600 italic'>
                      Please contact the shop to check availability
                    </p>
                  </div>
                )}

                <section className='mt-4 md:mt-4 w-full max-w-2xs [ md:max-w-full pt-4 border-t border-black ] flex flex-row justify-between md:space-x-8 md:justify-start'>
                  <span className='hidden md:block my-auto text-gray-500'>
                    Share:
                  </span>
                  <SharingBar
                    className='h-6'
                    url={`/book/${book.isbn}`}
                    text={`I thought you'd like this book: ${book.title} - ${book.author}`}
                  />
                </section>
                <section className='w-full mb-auto mt-8'>
                  <ul className='flex flex-row flex-wrap justify-start space-x-2'>
                    {book.category_books.map((c, i) => (
                      <li key={i} className='mb-1 flex'>
                        <Link href={`/browse/${c.category.slug}`}>
                          <a className='px-4 py-1 border border-gray-500 text-gray-500 rounded-full hover:bg-gray-500 hover:text-white '>
                            {c.category.name}
                          </a>
                        </Link>
                      </li>
                    ))}
                    {book.tag_books.map(
                      (t, i) =>
                        !book.category_books.some(
                          (c) => c.category.name === t.tag.text
                        ) && (
                          <li
                            key={i}
                            className='py-1 px-4 border border-gray-400 text-gray-400 rounded-full mb-1'>
                            {t.tag.text}
                          </li>
                        )
                    )}
                  </ul>
                </section>
              </section>
              {book.description && book.description !== "" && (
                <section className='prose px-8 md:col-span-5 md:col-start-1 justify-self-start md:px-0  [ lg:col-start-3 lg:col-span-3 lg:row-start-1 ] '>
                  <RenderContent content={book.description} />
                </section>
              )}
            </article>

            {recs?.books_by_pk?.post_books.length > 0 && (
              <ListReccomendations
                lists={recs.books_by_pk.post_books
                  .map((p) => p.post)
                  .filter((o) => o)}
              />
            )}
            {recs?.event_book?.length > 0 && (
              <EventRecommendations
                events={recs.event_book.map((eb) => eb.event).filter((o) => o)}
              />
            )}

            <BookReccomendations
              books={recs?.books}
              handleMore={() => setOffset(offset + 1)}
              loading={recLoading}
            />
          </>
        ) : (
          "not found"
        )}
      </Layout>
    </>
  );
};

export default Book;
